import React, { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Text, TextButton } from '@amzn/storm-ui';
import isEmpty from 'lodash/isEmpty';
import useIntl from '@i18n/useIntl';
import InputController from '@components/form/InputController';
import DropdownController from '@components/form/DropdownController';
import * as formConstants from '@components/form/constants';
import {
  ACCOUNT_INDUSTRY_FIELD_LABEL,
  ACCOUNT_INDUSTRY_FIELD_ID,
  ACCOUNT_INDUSTRY_FIELD_CONTROLLER_NAME,
  ACCOUNT_TIME_ZONE_FIELD_LABEL,
  ACCOUNT_TIME_ZONE_FIELD_ID,
  ACCOUNT_TIME_ZONE_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_NAME_FIELD_LABEL,
  BUSINESS_DETAILS_NAME_FIELD_ID,
  BUSINESS_DETAILS_NAME_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_NAME_FIELD_TOOLTIP_TRANSLATION_KEY,
  BUSINESS_DETAILS_PHONE_FIELD_LABEL,
  BUSINESS_DETAILS_PHONE_FIELD_TOOLTIP_TRANSLATION_KEY,
  BUSINESS_DETAILS_PHONE_FIELD_ID,
  BUSINESS_DETAILS_PHONE_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_WEBSITE_FIELD_LABEL,
  BUSINESS_DETAILS_WEBSITE_FIELD_ID,
  BUSINESS_DETAILS_WEBSITE_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_WEBSITE_FIELD_TOOLTIP_TRANSLATION_KEY,
  BUSINESS_DETAILS_COUNTRY_FIELD_LABEL,
  BUSINESS_DETAILS_COUNTRY_DROPDOWN_PLACEHOLDER,
  BUSINESS_DETAILS_COUNTRY_FIELD_ID,
  BUSINESS_DETAILS_COUNTRY_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_ADDRESS_LINE1_FIELD_LABEL,
  BUSINESS_DETAILS_ADDRESS_LINE1_FIELD_ID,
  BUSINESS_DETAILS_ADDRESS_LINE1_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_ADDRESS_LINE2_FIELD_LABEL,
  BUSINESS_DETAILS_ADDRESS_LINE2_FIELD_ID,
  BUSINESS_DETAILS_ADDRESS_LINE2_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_CITY_FIELD_LABEL,
  BUSINESS_DETAILS_CITY_FIELD_ID,
  BUSINESS_DETAILS_CITY_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_STATE_FIELD_LABEL,
  BUSINESS_DETAILS_STATE_DROPDOWN_PLACEHOLDER,
  BUSINESS_DETAILS_STATE_FIELD_ID,
  BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_POSTAL_CODE_FIELD_LABEL,
  BUSINESS_DETAILS_POSTAL_CODE_FIELD_ID,
  BUSINESS_DETAILS_POSTAL_CODE_FIELD_CONTROLLER_NAME,
} from '@features/account-settings/constants';
import { useGetCountriesQuery, useGetStatesQuery } from '@services/address';
import { Enumerations } from 'src/shared/models/account';
import {
  MANAGER_ACCOUNT_CREATION_BUSINESS_SECTION_DESCRIPTION_TRANSLATION_KEY,
  MANAGER_ACCOUNT_CREATION_COUNTRY_LOAD_ERROR_TRANSLATION_KEY,
  MANAGER_ACCOUNT_CREATION_COUNTRY_LOAD_RETRY_TRANSLATION_KEY,
  MANAGER_ACCOUNT_CREATION_ENUMERATIONS_LOAD_ERROR_TRANSLATION_KEY,
  MANAGER_ACCOUNT_CREATION_STATE_LOAD_ERROR_TRANSLATION_KEY,
  MANAGER_ACCOUNT_CREATION_TIME_ZONE_PLACEHOLDER_TRANSLATION_KEY,
  MANAGER_ACCOUNT_CREATION_INDUSTRY_PLACEHOLDER_TRANSLATION_KEY,
} from '../constants';

interface BusinessDetailsSectionProps {
  enumerations?: Enumerations;
  isFetchingEnumerations: boolean;
  isEnumerationsError: boolean;
  refetchEnumerations: () => void;
}

/**
 * Business details for manager account creation. The address fields are
 * required because GRS creates the billing profile from them; address line
 * 2 is optional. Timezone and industry sit in this section visually but are
 * attributes of the manager account itself rather than of the billing
 * address; both are required.
 *
 * Local to this page rather than reusing the account-settings
 * `BusinessDetailsControllers`: that component auto-fills currency/timezone
 * on country selection, which is wrong for this page.
 */
const BusinessDetailsSection: FC<BusinessDetailsSectionProps> = ({
  enumerations,
  isFetchingEnumerations,
  isEnumerationsError,
  refetchEnumerations,
}) => {
  const { intl } = useIntl();
  const { watch, setValue } = useFormContext();

  const countryCode = watch(BUSINESS_DETAILS_COUNTRY_FIELD_CONTROLLER_NAME);

  const {
    data: countries,
    isFetching: isFetchingCountryList,
    isError: isLoadingCountryError,
    refetch: refetchCountries,
  } = useGetCountriesQuery();

  const {
    data: states,
    isLoading: isLoadingStateList,
    isFetching: isFetchingStateList,
    isError: isLoadingStateError,
  } = useGetStatesQuery(countryCode, { skip: !countryCode });

  const requiredValidation = useMemo(
    () => ({
      required: intl.getMessage(
        formConstants.REQUIRED_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
      ),
    }),
    [intl],
  );

  const optionalTag = intl.getMessage(
    formConstants.OPTIONAL_TAG_TRANSLATION_KEY,
  );

  const countryChangeHandler = () => {
    // A state belongs to the country it was chosen under
    setValue(BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME, '');
  };

  /*
    "No country yet" and "loading" both count as "has states": rendering the
    optional text input in either window would let the user type a value that
    is silently discarded when the field flips to the dropdown. The text
    input is only for a selected country that genuinely has no states.
  */
  const isStatesLoading = isLoadingStateList || isFetchingStateList;
  const hasStates =
    !isLoadingStateError &&
    (!countryCode || isStatesLoading || !isEmpty(states));

  return (
    <>
      <Text color="secondary" size="small">
        {intl.getMessage(
          MANAGER_ACCOUNT_CREATION_BUSINESS_SECTION_DESCRIPTION_TRANSLATION_KEY,
        )}
      </Text>
      <InputController
        label={intl.getMessage(BUSINESS_DETAILS_NAME_FIELD_LABEL)}
        id={BUSINESS_DETAILS_NAME_FIELD_ID}
        controllerName={BUSINESS_DETAILS_NAME_FIELD_CONTROLLER_NAME}
        registerOptions={{ ...requiredValidation }}
        helpTipText={intl.getMessage(
          BUSINESS_DETAILS_NAME_FIELD_TOOLTIP_TRANSLATION_KEY,
        )}
      />
      <InputController
        label={intl.getMessage(BUSINESS_DETAILS_PHONE_FIELD_LABEL)}
        id={BUSINESS_DETAILS_PHONE_FIELD_ID}
        controllerName={BUSINESS_DETAILS_PHONE_FIELD_CONTROLLER_NAME}
        registerOptions={{
          ...requiredValidation,
          pattern: {
            value: formConstants.PHONE_VALIDATION,
            message: intl.getMessage(
              formConstants.PHONE_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
            ),
          },
        }}
        helpTipText={intl.getMessage(
          BUSINESS_DETAILS_PHONE_FIELD_TOOLTIP_TRANSLATION_KEY,
        )}
        type="tel"
      />
      <InputController
        label={intl.getMessage(BUSINESS_DETAILS_WEBSITE_FIELD_LABEL)}
        id={BUSINESS_DETAILS_WEBSITE_FIELD_ID}
        controllerName={BUSINESS_DETAILS_WEBSITE_FIELD_CONTROLLER_NAME}
        registerOptions={{
          ...requiredValidation,
          pattern: {
            value: formConstants.WEBSITE_VALIDATION,
            message: intl.getMessage(
              formConstants.WEBSITE_URL_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
            ),
          },
        }}
        helpTipText={intl.getMessage(
          BUSINESS_DETAILS_WEBSITE_FIELD_TOOLTIP_TRANSLATION_KEY,
        )}
      />
      <DropdownController
        label={intl.getMessage(BUSINESS_DETAILS_COUNTRY_FIELD_LABEL)}
        id={BUSINESS_DETAILS_COUNTRY_FIELD_ID}
        controllerName={BUSINESS_DETAILS_COUNTRY_FIELD_CONTROLLER_NAME}
        items={countries || []}
        registerOptions={{ ...requiredValidation }}
        overrideDropdownLabel={intl.getMessage(
          BUSINESS_DETAILS_COUNTRY_DROPDOWN_PLACEHOLDER,
        )}
        // isFetching rather than isLoading, so a retry after a failed
        // load also shows progress (isLoading covers only the first
        // request)
        isLoading={isFetchingCountryList}
        disabled={isLoadingCountryError}
        description={
          /*
            A failed country load leaves a disabled required field the
            user cannot fill, which would otherwise block submit with no
            recovery. The retry refetches the list in place; the note
            yields to the dropdown's spinner while the retry is in flight.
          */
          isLoadingCountryError && !isFetchingCountryList ? (
            <Text color="secondary" size="xsmall">
              {intl.getMessage(
                MANAGER_ACCOUNT_CREATION_COUNTRY_LOAD_ERROR_TRANSLATION_KEY,
              )}{' '}
              <TextButton onClick={() => refetchCountries()}>
                {intl.getMessage(
                  MANAGER_ACCOUNT_CREATION_COUNTRY_LOAD_RETRY_TRANSLATION_KEY,
                )}
              </TextButton>
            </Text>
          ) : undefined
        }
        callback={countryChangeHandler}
      />
      <InputController
        label={intl.getMessage(BUSINESS_DETAILS_ADDRESS_LINE1_FIELD_LABEL)}
        id={BUSINESS_DETAILS_ADDRESS_LINE1_FIELD_ID}
        controllerName={BUSINESS_DETAILS_ADDRESS_LINE1_FIELD_CONTROLLER_NAME}
        registerOptions={{ ...requiredValidation }}
      />
      <InputController
        label={intl.getMessage(BUSINESS_DETAILS_ADDRESS_LINE2_FIELD_LABEL)}
        id={BUSINESS_DETAILS_ADDRESS_LINE2_FIELD_ID}
        controllerName={BUSINESS_DETAILS_ADDRESS_LINE2_FIELD_CONTROLLER_NAME}
        optionalTag={optionalTag}
      />
      <InputController
        label={intl.getMessage(BUSINESS_DETAILS_CITY_FIELD_LABEL)}
        id={BUSINESS_DETAILS_CITY_FIELD_ID}
        controllerName={BUSINESS_DETAILS_CITY_FIELD_CONTROLLER_NAME}
        registerOptions={{ ...requiredValidation }}
      />
      {hasStates ? (
        <DropdownController
          label={intl.getMessage(BUSINESS_DETAILS_STATE_FIELD_LABEL)}
          id={BUSINESS_DETAILS_STATE_FIELD_ID}
          controllerName={BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME}
          items={states || []}
          // Required only once a country is chosen: before that the
          // dropdown is disabled, and a validation error pointing at a
          // control the user cannot interact with would misdirect them
          // away from the country field
          registerOptions={countryCode ? { ...requiredValidation } : {}}
          overrideDropdownLabel={intl.getMessage(
            BUSINESS_DETAILS_STATE_DROPDOWN_PLACEHOLDER,
          )}
          isLoading={isStatesLoading}
          disabled={!countryCode}
        />
      ) : (
        <>
          {isLoadingStateError && (
            /*
              The failed dropdown degrades to free text rather than
              silently looking like a no-states country: the user can
              still type the state, and this note says why the list is
              missing. Deliberately optional, like the no-states branch
              it shares: requiring it would force a value for countries
              that genuinely have none, and Address Service remains the
              validator either way.
            */
            <Text color="secondary" size="xsmall">
              {intl.getMessage(
                MANAGER_ACCOUNT_CREATION_STATE_LOAD_ERROR_TRANSLATION_KEY,
              )}
            </Text>
          )}
          <InputController
            label={intl.getMessage(BUSINESS_DETAILS_STATE_FIELD_LABEL)}
            id={BUSINESS_DETAILS_STATE_FIELD_ID}
            controllerName={BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME}
            optionalTag={optionalTag}
          />
        </>
      )}
      <InputController
        label={intl.getMessage(BUSINESS_DETAILS_POSTAL_CODE_FIELD_LABEL)}
        id={BUSINESS_DETAILS_POSTAL_CODE_FIELD_ID}
        controllerName={BUSINESS_DETAILS_POSTAL_CODE_FIELD_CONTROLLER_NAME}
        registerOptions={{
          ...requiredValidation,
          pattern: {
            value: formConstants.POSTAL_CODE_VALIDATION,
            message: intl.getMessage(
              formConstants.NUMBER_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
            ),
          },
        }}
      />
      <DropdownController
        label={intl.getMessage(ACCOUNT_TIME_ZONE_FIELD_LABEL)}
        id={ACCOUNT_TIME_ZONE_FIELD_ID}
        controllerName={ACCOUNT_TIME_ZONE_FIELD_CONTROLLER_NAME}
        items={enumerations?.timeZones || []}
        registerOptions={{ ...requiredValidation }}
        isLoading={isFetchingEnumerations}
        disabled={isEnumerationsError}
        overrideDropdownLabel={intl.getMessage(
          MANAGER_ACCOUNT_CREATION_TIME_ZONE_PLACEHOLDER_TRANSLATION_KEY,
        )}
        description={
          /*
            Same recovery as the country list: both dropdowns below are
            required and come from one request, so a failed load would
            otherwise leave two disabled fields the user cannot fill.
          */
          isEnumerationsError && !isFetchingEnumerations ? (
            <Text color="secondary" size="xsmall">
              {intl.getMessage(
                MANAGER_ACCOUNT_CREATION_ENUMERATIONS_LOAD_ERROR_TRANSLATION_KEY,
              )}{' '}
              <TextButton onClick={() => refetchEnumerations()}>
                {intl.getMessage(
                  MANAGER_ACCOUNT_CREATION_COUNTRY_LOAD_RETRY_TRANSLATION_KEY,
                )}
              </TextButton>
            </Text>
          ) : undefined
        }
      />
      <DropdownController
        label={intl.getMessage(ACCOUNT_INDUSTRY_FIELD_LABEL)}
        id={ACCOUNT_INDUSTRY_FIELD_ID}
        controllerName={ACCOUNT_INDUSTRY_FIELD_CONTROLLER_NAME}
        items={enumerations?.industries || []}
        registerOptions={{ ...requiredValidation }}
        isLoading={isFetchingEnumerations}
        disabled={isEnumerationsError}
        overrideDropdownLabel={intl.getMessage(
          MANAGER_ACCOUNT_CREATION_INDUSTRY_PLACEHOLDER_TRANSLATION_KEY,
        )}
      />
    </>
  );
};

export default BusinessDetailsSection;
